import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { TranslateModule } from '@ngx-translate/core'
import { FormsModule } from '@angular/forms'
import {
  BoardMemberLabelling,
  CallsService,
  GoverningBodyType,
  MinutesAnonymization,
  MinutesPassive,
  MinutesStyling,
  MinutesVerboseness,
  MinutesNameFormat,
  MinutesDateFormat,
  MinutesTimeFormat,
  MinutesLanguage,
  MinutesAttribution,
} from '../calls.service'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { MetricsService } from '../metrics.service'
import { CommonModule } from '@angular/common'

export interface MinuteStyleOption<T> {
  value: T
  label: string
}

export interface MinuteStyleOptionWithExample<T> extends MinuteStyleOption<T> {
  example: string
}

const ALL_LANGUAGE_OPTIONS: MinuteStyleOption<MinutesLanguage>[] = [
  { value: MinutesLanguage.BRITISH, label: 'MINUTES_STYLING.BRITISH_ENGLISH' },
  { value: MinutesLanguage.AMERICAN, label: 'MINUTES_STYLING.AMERICAN_ENGLISH' },
  { value: MinutesLanguage.SPANISH, label: 'MINUTES_STYLING.SPANISH' },
]

@Component({
  selector: 'app-minutes-styling',
  standalone: true,
  imports: [TranslateModule, FormsModule, MatIcon, MatTooltip, CommonModule],
  templateUrl: './minutes-styling.component.html',
  styleUrl: './minutes-styling.component.scss',
})
export class MinutesStylingComponent implements OnChanges {
  @Input()
  disabled = false

  @Input()
  namingDisabled = false

  @Input()
  honorificDisabled = false

  @Input()
  enabledMinutesLanguages: string[] = ['BRITISH', 'AMERICAN']

  @Input()
  showAllLanguages = false

  @Input()
  governingBodyType: GoverningBodyType | null = null

  @Input()
  attendeeListHasMembersAndNonMembers = false

  @Input()
  data: MinutesStyling = {
    minutesVerboseness: MinutesVerboseness.DETAILED,
    minutesAnonymization: MinutesAnonymization.NONE,
    minutesAnonymizationMembers: MinutesAnonymization.NONE,
    minutesAnonymizationAttendees: MinutesAnonymization.NONE,
    minutesPassive: MinutesPassive.CONTEXTUAL,
    minutesNameFormat: MinutesNameFormat.FULL_NAME,
    minutesNameFormatMembers: MinutesNameFormat.FULL_NAME,
    minutesNameFormatAttendees: MinutesNameFormat.FULL_NAME,
    minutesDateFormat: MinutesDateFormat.DD_MONTH_YYYY,
    minutesTimeFormat: MinutesTimeFormat.TWENTY_FOUR_HOUR,
    minutesLanguage: MinutesLanguage.BRITISH,
    minutesAttribution: MinutesAttribution.CONTEXTUAL,
    minutesAttributionMembers: MinutesAttribution.CONTEXTUAL,
    minutesAttributionAttendees: MinutesAttribution.CONTEXTUAL,
    boardMemberLabelling: BoardMemberLabelling.LABEL,
  }

  @Output()
  update = new EventEmitter<Partial<MinutesStyling>>()

  callsService = inject(CallsService)
  metricsService = inject(MetricsService)

  nameFormatDropdownOpen = false
  memberNameFormatDropdownOpen = false
  attendeeNameFormatDropdownOpen = false

  verbosenesOptions: MinuteStyleOption<MinutesVerboseness>[] = [
    { value: MinutesVerboseness.CONCISE, label: 'MINUTES_STYLING.CONCISE' },
    { value: MinutesVerboseness.DETAILED, label: 'MINUTES_STYLING.DETAILED' },
    { value: MinutesVerboseness.EXHAUSTIVE, label: 'MINUTES_STYLING.EXHAUSTIVE' },
  ]

  voiceOptions: MinuteStyleOption<MinutesPassive>[] = [
    { value: MinutesPassive.ACTIVE, label: 'MINUTES_STYLING.ACTIVE' },
    { value: MinutesPassive.CONTEXTUAL, label: 'MINUTES_STYLING.CONTEXTUAL' },
    { value: MinutesPassive.PASSIVE, label: 'MINUTES_STYLING.PASSIVE' },
  ]

  attributionOptions: MinuteStyleOption<MinutesAnonymization>[] = [
    { value: MinutesAnonymization.NONE, label: 'MINUTES_STYLING.WITH_NAMES' },
    { value: MinutesAnonymization.ALL, label: 'MINUTES_STYLING.ANONYMIZED' },
  ]

  attributionStyleOptions: MinuteStyleOption<MinutesAttribution>[] = [
    { value: MinutesAttribution.OUTCOME, label: 'MINUTES_STYLING.OUTCOME_LED' },
    { value: MinutesAttribution.COLLECTIVE, label: 'MINUTES_STYLING.COLLECTIVE_VOICE' },
    { value: MinutesAttribution.CONTEXTUAL, label: 'MINUTES_STYLING.CONTEXTUAL' },
  ]

  boardMemberLabellingOptions: MinuteStyleOption<BoardMemberLabelling>[] = [
    { value: BoardMemberLabelling.LABEL, label: 'MINUTES_STYLING.BOARD_MEMBER_LABEL' },
    { value: BoardMemberLabelling.ANONYMIZE, label: 'MINUTES_STYLING.BOARD_MEMBER_ANONYMIZE' },
  ]

  nameFormats: MinuteStyleOptionWithExample<MinutesNameFormat>[] = [
    { value: MinutesNameFormat.FIRST_NAME, label: 'MINUTES_STYLING.FIRST_NAME', example: 'MINUTES_STYLING.EXAMPLE_FIRST_NAME' },
    { value: MinutesNameFormat.ROLE, label: 'MINUTES_STYLING.ROLE', example: 'MINUTES_STYLING.EXAMPLE_ROLE' },
    { value: MinutesNameFormat.SURNAME, label: 'MINUTES_STYLING.SURNAME', example: 'MINUTES_STYLING.EXAMPLE_SURNAME' },
    { value: MinutesNameFormat.INITIALS, label: 'MINUTES_STYLING.INITIALS', example: 'MINUTES_STYLING.EXAMPLE_INITIALS' },
    { value: MinutesNameFormat.FULL_NAME, label: 'MINUTES_STYLING.FULL_NAME', example: 'MINUTES_STYLING.EXAMPLE_FULL_NAME' },
    { value: MinutesNameFormat.INITIAL_SURNAME, label: 'MINUTES_STYLING.INITIAL_SURNAME', example: 'MINUTES_STYLING.EXAMPLE_INITIAL_SURNAME' },
    { value: MinutesNameFormat.HONORIFIC_SURNAME, label: 'MINUTES_STYLING.HONORIFIC_SURNAME', example: 'MINUTES_STYLING.EXAMPLE_HONORIFIC_SURNAME' },
  ]

  dateFormats: MinuteStyleOption<MinutesDateFormat>[] = [
    { value: MinutesDateFormat.DD_MONTH_YYYY, label: 'MINUTES_STYLING.DATE_FORMAT_DD_MONTH_YYYY' },
    { value: MinutesDateFormat.MONTH_DD_TH_YYYY, label: 'MINUTES_STYLING.DATE_FORMAT_MONTH_DD_TH_YYYY' },
    { value: MinutesDateFormat.DD_MM_YYYY_DOT, label: 'MINUTES_STYLING.DATE_FORMAT_DD_MM_YYYY_DOT' },
    { value: MinutesDateFormat.DD_MM_YYYY_SLASH, label: 'MINUTES_STYLING.DATE_FORMAT_DD_MM_YYYY_SLASH' },
  ]

  timeFormats: MinuteStyleOption<MinutesTimeFormat>[] = [
    { value: MinutesTimeFormat.TWENTY_FOUR_HOUR, label: 'MINUTES_STYLING.TIME_FORMAT_TWENTY_FOUR_HOUR' },
    { value: MinutesTimeFormat.TWELVE_HOUR, label: 'MINUTES_STYLING.TIME_FORMAT_TWELVE_HOUR' },
  ]

  languageOptions: MinuteStyleOption<MinutesLanguage>[] = ALL_LANGUAGE_OPTIONS.slice(0, 2)

  ngOnChanges(changes: SimpleChanges) {
    if (changes['enabledMinutesLanguages'] || changes['data'] || changes['showAllLanguages']) {
      const availableLanguages = this.showAllLanguages ? ALL_LANGUAGE_OPTIONS : ALL_LANGUAGE_OPTIONS.filter((opt) => opt.value !== MinutesLanguage.SPANISH)
      this.languageOptions = availableLanguages.filter((opt) => this.enabledMinutesLanguages.includes(opt.value))
      if (this.data.minutesLanguage && !this.enabledMinutesLanguages.includes(this.data.minutesLanguage)) {
        this.sendUpdate('minutesLanguage', MinutesLanguage.BRITISH)
      }
    }
  }

  getSelectedNameFormat(): MinuteStyleOptionWithExample<MinutesNameFormat> {
    return this.getNameFormatOption(this.data.minutesNameFormat)
  }

  getSelectedMemberNameFormat(): MinuteStyleOptionWithExample<MinutesNameFormat> {
    return this.getNameFormatOption(this.data.minutesNameFormatMembers)
  }

  getSelectedAttendeeNameFormat(): MinuteStyleOptionWithExample<MinutesNameFormat> {
    return this.getNameFormatOption(this.data.minutesNameFormatAttendees)
  }

  private getNameFormatOption(value: MinutesNameFormat | null): MinuteStyleOptionWithExample<MinutesNameFormat> {
    return this.nameFormats.find((format) => format.value === value) || this.nameFormats[0]
  }

  sendUpdate(field: string, value: any) {
    const newStyles = {
      [field]: value,
    }
    this.update.emit(newStyles)
  }

  selectRadioOption(settingName: string, value: any) {
    this.sendUpdate(settingName, value)
  }

  selectNameFormat(format: MinuteStyleOptionWithExample<MinutesNameFormat>) {
    this.sendUpdate('minutesNameFormat', format.value)
    this.nameFormatDropdownOpen = false
  }

  selectMemberNameFormat(format: MinuteStyleOptionWithExample<MinutesNameFormat>) {
    this.sendUpdate('minutesNameFormatMembers', format.value)
    this.memberNameFormatDropdownOpen = false
  }

  selectAttendeeNameFormat(format: MinuteStyleOptionWithExample<MinutesNameFormat>) {
    this.sendUpdate('minutesNameFormatAttendees', format.value)
    this.attendeeNameFormatDropdownOpen = false
  }

  toggleDropdown() {
    this.nameFormatDropdownOpen = !this.nameFormatDropdownOpen
  }

  toggleMemberDropdown() {
    this.memberNameFormatDropdownOpen = !this.memberNameFormatDropdownOpen
  }

  toggleAttendeeDropdown() {
    this.attendeeNameFormatDropdownOpen = !this.attendeeNameFormatDropdownOpen
  }

  closeDropdown() {
    this.nameFormatDropdownOpen = false
    this.memberNameFormatDropdownOpen = false
    this.attendeeNameFormatDropdownOpen = false
  }

  get isAnonimizationActive(): boolean {
    return this.data.minutesAnonymization === MinutesAnonymization.ALL
  }

  get isMemberAnonymizationActive(): boolean {
    return this.data.minutesAnonymizationMembers === MinutesAnonymization.ALL
  }

  get isAttendeeAnonymizationActive(): boolean {
    return this.data.minutesAnonymizationAttendees === MinutesAnonymization.ALL
  }

  get showMemberHonorificWarning(): boolean {
    return this.data.minutesNameFormatMembers === MinutesNameFormat.HONORIFIC_SURNAME && this.honorificDisabled
  }

  get showAttendeeHonorificWarning(): boolean {
    return this.data.minutesNameFormatAttendees === MinutesNameFormat.HONORIFIC_SURNAME && this.honorificDisabled
  }

  get showBoardMemberLabelling(): boolean {
    // Temporarily disabled — feature is implemented but hidden until ready for release
    return false
  }

  isNameFormatDisabled(format: MinuteStyleOptionWithExample<MinutesNameFormat>): boolean {
    if (format.value === MinutesNameFormat.HONORIFIC_SURNAME) {
      return this.honorificDisabled
    }
    return false
  }

  get showHonorificWarning(): boolean {
    return this.data.minutesNameFormat === MinutesNameFormat.HONORIFIC_SURNAME && this.honorificDisabled
  }
}
